import service from '@/store/services/utils/settings-service';

const state = {
    settings: [],
    setting: null,
    overrides: [],
    meta: {},
    isLoading: false,
    error: null,
    categories: [],
    overrideLevels: ['user', 'staff', 'department', 'workplace', 'station']
};

const mutations = {
    SET_SETTINGS(state, settings) {
        state.settings = settings;
    },
    SET_SETTING(state, setting) {
        state.setting = setting;
    },
    SET_OVERRIDES(state, overrides) {
        state.overrides = overrides;
    },
    SET_META(state, meta) {
        state.meta = meta;
    },
    SET_LOADING(state, status) {
        state.isLoading = status;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CATEGORIES(state, categories) {
        state.categories = categories;
    },
    UPDATE_SETTING(state, payload) {
        let index = state.settings.findIndex(function(s) { return s.key === payload.key; });
        if (index !== -1) {
            state.settings= payload;
        }
    },
    ADD_OVERRIDE(state, override) {
        state.overrides.push(override);
    },
    REMOVE_OVERRIDE(state, overrideId) {
        state.overrides = state.overrides.filter(function(o) { return o.id !== overrideId; });
    }
};

const actions = {
    fetchSettings: function({ commit, state }, params) {
        commit('SET_LOADING', true);
        return service.listSettings(params || {})
            .then(function(response) {
                commit('SET_SETTINGS', response.data || []);
                commit('SET_META', response.meta || {});
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    fetchSetting: function({ commit }, key) {
        commit('SET_LOADING', true);
        return service.getSetting(key)
            .then(function(response) {
                commit('SET_SETTING', response);
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    updateSetting: function({ commit }, payload) {
        commit('SET_LOADING', true);
        return service.updateSetting(payload.key, payload)
            .then(function(response) {
                commit('UPDATE_SETTING', response);
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    fetchOverrides: function({ commit }, key) {
        commit('SET_LOADING', true);
        return service.getSettingOverrides(key)
            .then(function(response) {
                commit('SET_OVERRIDES', response || []);
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    createOverride: function({ commit }, payload) {
        commit('SET_LOADING', true);
        return service.createOverride(payload)
            .then(function(response) {
                commit('ADD_OVERRIDE', response);
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    deleteOverride: function({ commit }, overrideId) {
        commit('SET_LOADING', true);
        return service.deleteOverride(overrideId)
            .then(function() {
                commit('REMOVE_OVERRIDE', overrideId);
                commit('SET_LOADING', false);
                return true;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    resetToDefault: function({ commit }, key) {
        commit('SET_LOADING', true);
        return service.resetSetting(key)
            .then(function(response) {
                commit('UPDATE_SETTING', response);
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    initializeDefaults: function({ commit }) {
        commit('SET_LOADING', true);
        return service.initializeDefaults()
            .then(function(response) {
                commit('SET_LOADING', false);
                return response;
            })
            .catch(function(error) {
                commit('SET_LOADING', false);
                commit('SET_ERROR', error.message);
                throw error;
            });
    },

    clearError: function({ commit }) {
        commit('SET_ERROR', null);
    }
};

const getters = {
    settings: function (state) {
        return state.settings;
    },
    setting: function (state) {
        return state.setting;
    },
    overrides: function (state) {
        return state.overrides;
    },
    meta: function (state) {
        return state.meta;
    },
    isLoading: function (state) {
        return state.isLoading;
    },
    error: function (state) {
        return state.error;
    },
    categories: function (state) {
        return state.categories;
    },
    overrideLevels: function (state) {
        return state.overrideLevels;
    },
    settingsByCategory: function (state) {
        let grouped = {};
        state.settings.forEach(function (setting) {
            const category = setting.category || 'general';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(setting);
        });
        return grouped;
    },
    getSettingByKey: function (state) {
        return function (key) {
            return state.settings.find(function (s) {
                return s.key === key;
            });
        };
    }
};

export default {
    namespaced: true,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
};